import { CDN } from '../Models/CDN';

// google.maps.Marker must not be referenced at module scope (extends/static field
// initializers run at import time) since Google Maps isn't loaded on every page that
// bundles this module. The actual marker class is built lazily on first use.
//
// IListingMapMarker is only a type — types are erased at compile time, so referencing
// google.maps.Marker here (unlike the runtime `class extends` below) has no import-time
// footprint and doesn't reintroduce that problem.
export interface IListingMapMarker extends google.maps.Marker {
    id: string;
    iconType: string;
}

const _path = `${CDN.StaticUrl}/img/consumer/mapPins`;
const _markerIconDefault: any = {
    default : `${_path}/Default/unviewed-basic-pin.png`,
    favorite: `${_path}/Default/unviewed-like-pin.png`,
    default_pending: `${_path}/Orange/unviewed-basic-pin-v4.png`,
    favorite_pending: `${_path}/Orange/unviewed-like-pin-v4.png`,
    default_pending_feasibility: `${_path}/Blue/unviewed-basic-pin-v2.png`,
    favorite_pending_feasibility: `${_path}/Blue/unviewed-like-pin-v2.png`
};
const _markerIconSelected: any = {
    default: `${_path}/Default/focus-basic-pin.png`,
    favorite: `${_path}/Default/focus-like-pin.png`,
    default_pending: `${_path}/Orange/focus-basic-pin-v4.png`,
    favorite_pending: `${_path}/Orange/focus-like-pin-v4.png`,
    default_pending_feasibility: `${_path}/Blue/focus-basic-pin-v2.png`,
    favorite_pending_feasibility: `${_path}/Blue/focus-like-pin-v2.png`
};
const _markerIconHighlighted: any = {
    default: `${_path}/Default/hover-basic-pin.png`,
    favorite: `${_path}/Default/hover-like-pin.png` ,
    default_pending: `${_path}/Orange/hover-basic-pin-v4.png`,
    favorite_pending: `${_path}/Orange/hover-like-pin-v4.png`,
    default_pending_feasibility: `${_path}/Blue/hover-basic-pin-v2.png`,
    favorite_pending_feasibility: `${_path}/Blue/hover-like-pin-v2.png`
};
const _markerIconViewed: any = {
    default: `${_path}/Default/seen-basic-pin.png`,
    favorite: `${_path}/Default/seen-like-pin.png`,
    default_pending: `${_path}/Orange/seen-basic-pin-v4.png`,
    favorite_pending: `${_path}/Orange/seen-like-pin-v4.png`,
    default_pending_feasibility: `${_path}/Blue/seen-basic-pin-v2.png`,
    favorite_pending_feasibility: `${_path}/Blue/seen-like-pin-v2.png`
};
const _labelColor: string = "white";
const _labelPlacement: any = { x: 20, y: 11 };
const _labelFontSize: string = "10";

function getDisplayPrice(price: string): string {
    let priceValue: number = parseInt(price);
    let priceString: string = price.toString();
    if (priceValue >= 1000000) {
        let tempPrice = priceString.substr(0, priceString.length - 5);
        let beforeDecimal = tempPrice.substr(0, tempPrice.length - 1);
        let afterDecimal = tempPrice.substr(tempPrice.length -1, 1);
        if (afterDecimal !== "0") {
            tempPrice = beforeDecimal + "." + afterDecimal;
        } else {
            tempPrice = beforeDecimal;
        }
        priceString = tempPrice + "M";
    }
    else if (priceValue < 1000000 && priceValue > 999) {
        priceString = priceString.substr(0, priceString.length - 3) + "K";
    }
    else {
        priceString = "< 1K";
    }
    return priceString;
}

function getAlternateIcon(iconUrl: string): string {
    const iconSets = [
        _markerIconDefault,
        _markerIconHighlighted,
        _markerIconSelected,
        _markerIconViewed
    ];
    const alternates: { [key: string]: string } = {
        default: "favorite",
        favorite: "default",
        default_pending: "favorite_pending",
        favorite_pending: "default_pending",
        default_pending_feasibility: "favorite_pending_feasibility",
        favorite_pending_feasibility: "default_pending_feasibility"
    };

    for (const set of iconSets) {
        for (const key in alternates) {
            if (iconUrl === set[key]) {
                return set[alternates[key]];
            }
        }
    }
    return "";
}

let _ctor: any = null;
function getCtor(): any {
    if (!_ctor) {
        _ctor = class extends google.maps.Marker {
            public id: string;
            iconType: string = "default";

            constructor(map: any, lat: any, lng: any, iconType: any, price: any, pdid: any, address: string, zIndex?: number) {
                super({
                    map: map,
                    position: { lat: lat, lng: lng },
                    // title: address, // NWMLS whined about this, so we can't show it now.
                });

                this.id = pdid;
                price = getDisplayPrice(price);
                this.setLabel({ color: _labelColor, fontSize: _labelFontSize, text : price});
                this.setIcon({ url : _markerIconDefault[iconType],
                    scaledSize: new google.maps.Size(40, 24),
                    labelOrigin: new google.maps.Point(_labelPlacement["x"], _labelPlacement["y"])
                });
                if (!zIndex) { zIndex = 1; }
                this.setZIndex(zIndex);
                this.iconType = iconType;
            }
        };
    }
    return _ctor;
}

export const ListingMapMarker = {
    _markerIconDefault,
    _markerIconSelected,
    _markerIconHighlighted,
    _markerIconViewed,
    _labelPlacement,
    getAlternateIcon,
    getMarkerScaledSize: (): any => new google.maps.Size(40, 24),
    create: (map: any, lat: any, lng: any, iconType: any, price: any, pdid: any, address: string, zIndex?: number): IListingMapMarker => {
        const Ctor = getCtor();
        return new Ctor(map, lat, lng, iconType, price, pdid, address, zIndex);
    }
};
